import { Megaphone, PhoneIncoming, X } from "lucide-react";
import { useEffect, useState } from "react";
import type { OutboundScreenPop } from "@/shared/api/outboundApi";
import { Avatar } from "@/shared/ui/avatar";
import { IconButton } from "@/shared/ui/icon-button";
import { CALL_STATE } from "@/shared/ui/status";
import { StatusBadge } from "@/shared/ui/status-badge";
import { TimerDisplay } from "@/shared/ui/timer";
import { WS_DEST_USER_MESSAGES, onWsDestination, parseWsJson } from "@/shared/ws/wsBus";
import { PayloadView } from "./components";
import { formatPhone } from "./outboundLabels";

type Pop = OutboundScreenPop & { key: string; receivedAt: number };

const MAX_POPS = 3;

function isScreenPop(v: unknown): v is OutboundScreenPop {
  return !!v && typeof v === "object" && (v as { type?: unknown }).type === "OUTBOUND_SCREEN_POP";
}

/**
 * Screen pop do discador: escuta `/user/queue/messages` (conexão STOMP principal) e, quando chega um
 * `OUTBOUND_SCREEN_POP`, mostra um painel não bloqueante com os dados do cliente. Fica até ser fechado.
 */
export function OutboundScreenPopHost() {
  const [pops, setPops] = useState<Pop[]>([]);

  useEffect(
    () =>
      onWsDestination(WS_DEST_USER_MESSAGES, (body) => {
        const msg = parseWsJson(body);
        if (!isScreenPop(msg)) return;
        const key = `${msg.attemptId ?? ""}-${msg.contact?.id ?? ""}-${Date.now()}`;
        setPops((prev) => {
          // Mesma tentativa repetida → substitui.
          const rest = prev.filter((p) => msg.attemptId == null || p.attemptId !== msg.attemptId);
          return [{ ...msg, key, receivedAt: Date.now() }, ...rest].slice(0, MAX_POPS);
        });
      }),
    [],
  );

  if (!pops.length) return null;

  const dismiss = (key: string) => setPops((prev) => prev.filter((p) => p.key !== key));

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[150] flex w-[min(calc(100vw-2rem),24rem)] flex-col gap-3 sm:bottom-6 sm:right-6"
      aria-live="assertive"
    >
      {pops.map((p) => {
        const titleId = `pop-${p.key}`;
        return (
          <section
            key={p.key}
            role="alert"
            aria-labelledby={titleId}
            className="pm-toast-enter pointer-events-auto overflow-hidden rounded-xl border border-border bg-surface-raised text-fg shadow-lg"
          >
            <header className="flex items-start gap-3 border-b border-border bg-brand-subtle px-4 py-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-fg" aria-hidden>
                <PhoneIncoming className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-overline uppercase text-brand-accent">Ligação do discador</p>
                <p className="flex min-w-0 items-center gap-1.5 text-body-sm font-medium text-fg">
                  <Megaphone className="h-3.5 w-3.5 shrink-0 text-fg-subtle" aria-hidden />
                  <span className="truncate">{p.campaign?.name ?? "Campanha"}</span>
                </p>
              </div>
              <IconButton icon={X} label="Fechar" size="sm" tooltip={false} className="-mr-1.5 -mt-0.5" onClick={() => dismiss(p.key)} />
            </header>
            <div className="max-h-[50vh] space-y-3 overflow-y-auto px-4 py-3">
              <div className="flex items-start gap-3">
                <Avatar name={p.contact?.name || "Cliente"} />
                <div className="min-w-0 flex-1">
                  <p id={titleId} className="truncate text-title-sm text-fg">
                    {p.contact?.name || "Cliente"}
                  </p>
                  <p className="tnum font-mono text-body-sm text-fg-muted">{formatPhone(p.contact?.phone)}</p>
                  {p.contact?.externalId ? (
                    <p className="mt-0.5 truncate text-caption text-fg-subtle">
                      ID externo: <span className="font-mono">{p.contact.externalId}</span>
                    </p>
                  ) : null}
                </div>
                <StatusBadge meta={CALL_STATE.active} label="Conectada" />
              </div>
              <div className="rounded-lg border border-border bg-surface-sunken px-3 py-2.5">
                <PayloadView payload={p.contact?.payload} />
              </div>
              <div className="flex items-center justify-between gap-2 text-caption text-fg-subtle">
                <span className="tnum">
                  Recebido às{" "}
                  {new Date(p.receivedAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </span>
                <TimerDisplay since={p.receivedAt} size="sm" label="Tempo desde a conexão" />
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}
